// Griseskyting (pig hunt) — Tilleggsmodul (C, shooting). Pure geometry for
// drawing the hidden pig on the duel face and colouring hits on the reveal page.
// Mirror of backend/lib/pig-zone.js (which also places and scores); pigShape.json
// must stay byte-identical to backend/lib/pig-shape.json — keep the two in step.
// Spec: docs/superpowers/specs/2026-11-03-griseskyting-pig-hunt-design.md §4.
//
// Units: millimetres from the target centre, y up. Stored megalinkShot.x / .y are
// TENTHS of a millimetre (shotHitsPig converts).

import shape from './pigShape.json';

export const STAGE_TYPE = 'griseskyting';
export const BULLET_MM_DEFAULT = 5.6;
export const RAW_UNITS_PER_MM = 10;
// The drawn PNG (678 × 600 px canvas) at the body scale 548 px = 100 mm.
export const PIG_IMAGE_MM = { w: shape.imageMm.w, h: shape.imageMm.h };

export function isPigStage(stage) {
    const type = stage && typeof stage.type === 'string' ? stage.type.trim().toLowerCase() : '';
    return type === STAGE_TYPE;
}

// World polygon (mm, y up). mirrored = facing right.
export function pigPolygon({ cx = 0, cy = 0, mirrored = false } = {}) {
    return shape.points.map(([x, y]) => [cx + (mirrored ? -x : x), cy + y]);
}

function pointInPolygon([px, py], polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const crosses = (yi > py) !== (yj > py);
        if (crosses && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

function pointToSegment([px, py], [ax, ay], [bx, by]) {
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

// 0 when inside, else the distance to the nearest edge.
export function distanceToPolygon(point, polygon) {
    if (pointInPolygon(point, polygon)) return 0;
    let best = Infinity;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
        best = Math.min(best, pointToSegment(point, polygon[j], polygon[i]));
    }
    return best;
}

// Hit = the bullet hole touches the outline. x/y in mm.
export function pigHit({ x, y }, pig, bulletMm = BULLET_MM_DEFAULT) {
    return distanceToPolygon([x, y], pigPolygon(pig)) <= bulletMm / 2;
}

// Same test for a stored shot row (tenths of a millimetre).
export function shotHitsPig(shot, pig, bulletMm = BULLET_MM_DEFAULT) {
    const x = (Number(shot && shot.x) || 0) / RAW_UNITS_PER_MM;
    const y = (Number(shot && shot.y) || 0) / RAW_UNITS_PER_MM;
    return pigHit({ x, y }, pig, bulletMm);
}

// SVG path `d` for the outline in the ShotTarget coordinate system (y flipped when yUp).
export function pigPolygonPath(pig, yUp = true) {
    const flip = yUp ? -1 : 1;
    return `${pigPolygon(pig).map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y * flip}`).join(' ')} Z`;
}

// «3,5» — one decimal with a Norwegian decimal comma; '' when there is no value.
export function formatMm(value) {
    if (!Number.isFinite(value)) return '';
    return value.toFixed(1).replace('.', ',');
}
